"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.checkIfUserIsPendingApproverOfExpense = exports.getLastStepNotStarted = exports.compareApprovalLevelOfApprovalProcesses = exports.getStepInProgress = exports.getNextPendingApprovalByStatus = exports.getPendingApproverIdsOfStepInProgress = exports.getApproverIdsOfApprovalProcesses = exports.getApproverIdsOfWorkflow = exports.getApproverIdsOfApprovalProcess = exports.getApprovalProcessScopeFromPurchaseOrderStatus = exports.getApprovalProcessScopeFromExpenseStatus = exports.getApprovalProcessInProgress = exports.getApprovalProcess = void 0;
const models_1 = require("../models");
const approvalProcessesDiferenceLevel_enum_1 = require("../models/approvalProcess/approvalProcessesDiferenceLevel.enum");
const logicalExpression_utils_1 = require("./logicalExpression.utils");
const getApprovalProcess = (scope, approvalProcesses) => approvalProcesses.find((approvalProcess) => approvalProcess.scope === scope);
exports.getApprovalProcess = getApprovalProcess;
const getApprovalProcessInProgress = (approvalProcesses) => approvalProcesses.find((approvalProcess) => approvalProcess.status === models_1.ApprovalStatusEnum.IN_PROGRESS);
exports.getApprovalProcessInProgress = getApprovalProcessInProgress;
const getApprovalProcessScopeFromExpenseStatus = (expenseStatus, includeDocsNeededStatus = false) => expenseStatus === models_1.ExpenseStatusEnum.PURCHASE_PENDING
    ? models_1.ApprovalScopeEnum.EXPENSE_PURCHASE
    : [includeDocsNeededStatus ? models_1.ExpenseStatusEnum.DOCS_NEEDED : undefined, models_1.ExpenseStatusEnum.INVOICE_PENDING].includes(expenseStatus)
        ? models_1.ApprovalScopeEnum.EXPENSE_INVOICE
        : undefined;
exports.getApprovalProcessScopeFromExpenseStatus = getApprovalProcessScopeFromExpenseStatus;
const getApprovalProcessScopeFromPurchaseOrderStatus = (purchaseOrderStatus) => [models_1.PurchaseOrderStatusEnum.WAITING_FOR_APPROVAL, models_1.PurchaseOrderStatusEnum.DRAFT].includes(purchaseOrderStatus) ? models_1.ApprovalScopeEnum.PURCHASE_ORDER : undefined;
exports.getApprovalProcessScopeFromPurchaseOrderStatus = getApprovalProcessScopeFromPurchaseOrderStatus;
const getApproverIds = (approvalProcess) => {
    if (!approvalProcess) {
        return [];
    }
    const approverIds = [];
    for (const step of approvalProcess.steps) {
        if (step.status !== models_1.ApprovalStatusEnum.SKIPPED) {
            const approverIdsOfStep = (0, logicalExpression_utils_1.flattenExpression)(step.approvalExpression)
                .filter((approval) => { var _a; return (_a = approval.approver) === null || _a === void 0 ? void 0 : _a._id; })
                .map((approval) => { var _a; return String((_a = approval.approver) === null || _a === void 0 ? void 0 : _a._id); });
            approverIds.push(...approverIdsOfStep);
        }
    }
    return [...new Set(approverIds)];
};
const getWorkflowApproverIds = (approvalProcess) => {
    if (!approvalProcess) {
        return [];
    }
    const approverIds = [];
    for (const step of approvalProcess.steps) {
        const approverIdsOfStep = (0, logicalExpression_utils_1.flattenExpression)(step.approvalExpression)
            .filter((approval) => approval.workflowApprover)
            .map((approval) => String(approval.workflowApprover));
        approverIds.push(...approverIdsOfStep);
    }
    return [...new Set(approverIds)];
};
/**
 * @returns Array containing all the approver ids inside the approval process passed in.
 */
const getApproverIdsOfApprovalProcess = (approvalProcess) => getApproverIds(approvalProcess);
exports.getApproverIdsOfApprovalProcess = getApproverIdsOfApprovalProcess;
/**
 * @returns Array containing all the workflow approver ids inside the approval process passed in.
 */
const getApproverIdsOfWorkflow = (workflow) => getWorkflowApproverIds(workflow);
exports.getApproverIdsOfWorkflow = getApproverIdsOfWorkflow;
/**
 * @returns Array containing all the approver ids inside the approval processes passed in.
 */
const getApproverIdsOfApprovalProcesses = (approvalProcesses) => {
    const approverIds = [];
    for (const approvalProcess of approvalProcesses) {
        approverIds.push(...(0, exports.getApproverIdsOfApprovalProcess)(approvalProcess));
    }
    return [...new Set(approverIds)];
};
exports.getApproverIdsOfApprovalProcesses = getApproverIdsOfApprovalProcesses;
/**
 * @returns Array containing approver ids that have yet to approve in the current step in progress.
 */
const getPendingApproverIdsOfStepInProgress = (approvalProcess) => {
    if (!approvalProcess) {
        return [];
    }
    const currentStepInProgress = approvalProcess.steps.find(({ status }) => status === models_1.ApprovalStatusEnum.IN_PROGRESS);
    if (!currentStepInProgress) {
        return [];
    }
    const stepApprovals = (0, logicalExpression_utils_1.flattenExpression)(currentStepInProgress.approvalExpression);
    const pendingApprovalsOfStep = stepApprovals.filter((approval) => !approval.approvedBy && !approval.rejectedBy);
    const pendingApproverIdsOfStep = [...new Set(pendingApprovalsOfStep.map((approval) => approval.approver._id))];
    return pendingApproverIdsOfStep;
};
exports.getPendingApproverIdsOfStepInProgress = getPendingApproverIdsOfStepInProgress;
/**
 * Pending approval means that the approval hasn't been processed yet, that is, the approver hasn't approved nor rejected it.
 *
 * @statuses Statuses to look approval process by. Pass either status NOT_STARTED or IN_PROGRESS to get results. Other statuses will yield no result since in those statuses, the approvals should be processed.
 * @returns First pending approval whose step is in the provided status/es.
 */
const getNextPendingApprovalByStatus = (approvalProcess, statuses) => {
    const step = approvalProcess.steps.find(({ status }) => statuses.includes(status));
    if (!step) {
        return;
    }
    const approvals = (0, logicalExpression_utils_1.flattenExpression)(step.approvalExpression);
    return approvals.find((approval) => !approval.approvedBy && !approval.rejectedBy);
};
exports.getNextPendingApprovalByStatus = getNextPendingApprovalByStatus;
/**
 *
 * @param approvalProcess Approval process to look the active step of.
 * @returns object containing active step and its index
 */
const getStepInProgress = (approvalProcess) => {
    const stepInProgressIndex = approvalProcess.steps.findIndex((step) => step.status === models_1.ApprovalStatusEnum.IN_PROGRESS);
    const stepInProgress = approvalProcess.steps[stepInProgressIndex];
    return { step: stepInProgress, index: stepInProgressIndex };
};
exports.getStepInProgress = getStepInProgress;
/**
 * Compares the approval level between two approval processes. The approval processes should be identical and should both be in progress.
 * @param baseApprovalProcess - Base approval process to be compared.
 * @param targetApprovalProcess - Approval process to be compared against the first approval process.
 * @returns object Indicating if the target approval process is behind, equal or ahead of the base approval process in terms of processed approvals.
 */
const compareApprovalLevelOfApprovalProcesses = (baseApprovalProcess, targetApprovalProcess) => {
    const { step: baseStep, index: baseStepIndex } = (0, exports.getStepInProgress)(baseApprovalProcess);
    const { step: targetStep, index: targetStepIndex } = (0, exports.getStepInProgress)(targetApprovalProcess);
    if (!baseStep || !targetStep) {
        return;
    }
    if (targetStepIndex < baseStepIndex) {
        return { position: models_1.ComparisonPositionEnum.BEHIND, level: approvalProcessesDiferenceLevel_enum_1.ApprovalProcessesDiferenceLevelEnum.STEP };
    }
    if (targetStepIndex === baseStepIndex) {
        const baseProcessedApprovedByIds = (0, logicalExpression_utils_1.flattenExpression)(baseStep.approvalExpression)
            .filter((approval) => approval.approvedBy)
            .map((approval) => String(approval.approvedBy));
        const targetProcessedApprovedByIds = (0, logicalExpression_utils_1.flattenExpression)(targetStep.approvalExpression)
            .filter((approval) => approval.approvedBy)
            .map((approval) => String(approval.approvedBy));
        const hasBaseTheSameAmountOfApprovalsTheTargetHas = baseProcessedApprovedByIds.length === targetProcessedApprovedByIds.length;
        const hasBaseAllTheApprovalsTheTargetHas = baseProcessedApprovedByIds.every((id) => targetProcessedApprovedByIds.includes(id));
        // If the target approval process has the same amount of processed approvals and has all the approvals of the base approval process, then its "equal".
        if (hasBaseTheSameAmountOfApprovalsTheTargetHas && hasBaseAllTheApprovalsTheTargetHas) {
            return { position: models_1.ComparisonPositionEnum.EQUAL, level: approvalProcessesDiferenceLevel_enum_1.ApprovalProcessesDiferenceLevelEnum.APPROVAL };
        }
        // If the target approval process has all the approvals the base approval process has but has more processed approvals (marked as approved) then its "ahead" of the base approval process.
        if (!hasBaseTheSameAmountOfApprovalsTheTargetHas && hasBaseAllTheApprovalsTheTargetHas) {
            return { position: models_1.ComparisonPositionEnum.AHEAD, level: approvalProcessesDiferenceLevel_enum_1.ApprovalProcessesDiferenceLevelEnum.APPROVAL };
        }
        // If the target approval process does not have all the processed approvals of the base approval proces, it will be considered "behind", regardless if it has more processed approvals.
        return { position: models_1.ComparisonPositionEnum.BEHIND, level: approvalProcessesDiferenceLevel_enum_1.ApprovalProcessesDiferenceLevelEnum.APPROVAL };
    }
    // If the step of the expense is more than the folder then its "ahead" of the folder.
    return { position: models_1.ComparisonPositionEnum.AHEAD, level: approvalProcessesDiferenceLevel_enum_1.ApprovalProcessesDiferenceLevelEnum.STEP };
};
exports.compareApprovalLevelOfApprovalProcesses = compareApprovalLevelOfApprovalProcesses;
const getLastStepNotStarted = (approvalSteps) => {
    // TODO: Once we update js version we can simply use approvalSteps.findLast(x=> x.status === ApprovalStatusEnum.NOT_STARTED)
    for (let i = approvalSteps.length - 1; i >= 0; i--) {
        if (approvalSteps[i].status === models_1.ApprovalStatusEnum.NOT_STARTED) {
            return approvalSteps[i];
        }
    }
    return null;
};
exports.getLastStepNotStarted = getLastStepNotStarted;
const checkIfUserIsPendingApproverOfExpense = (approvalProcesses, userId) => {
    const approvalProcessInProgress = (0, exports.getApprovalProcessInProgress)(approvalProcesses);
    const pendingApproverIdsOfStepInProgress = (0, exports.getPendingApproverIdsOfStepInProgress)(approvalProcessInProgress);
    const isPendingApproverOfStepInProgress = pendingApproverIdsOfStepInProgress.some((id) => `${id}` === `${userId}`);
    return isPendingApproverOfStepInProgress;
};
exports.checkIfUserIsPendingApproverOfExpense = checkIfUserIsPendingApproverOfExpense;
